import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  InputAdornment,
  Divider,
  Link,
  CircularProgress
} from '@mui/material';
import {
  Email as EmailIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  PhoneIphone as PhoneIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { endUserApiClient } from '../utils/api';

const EmailOTPVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [info, setInfo] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [checkingSession, setCheckingSession] = useState(true);
  const [roomClosed, setRoomClosed] = useState(false);

  // URLパラメータから取得（iOSメールクライアント対策で互換キーと+表記補正に対応）
  const rawEmail = searchParams.get('email') || searchParams.get('e') || '';
  const rawPhone = searchParams.get('phone') || searchParams.get('p') || '';
  const rawRoom = searchParams.get('room') || searchParams.get('roomId') || searchParams.get('r') || '';
  // iOSメールの一部クライアントで+がスペースに変換されるケースに対応
  const email = (rawEmail && rawEmail.includes(' ') && rawEmail.includes('@')) ? rawEmail.replace(/\s+/g, '+') : rawEmail;
  const roomId = rawRoom;
  const phone = rawPhone ? rawPhone.replace(/\\s+/g, '') : '';
  const isPhoneMode = !!phone && !email;

  // 既存セッションがあればOTPをスキップしてチャットへ直接遷移
  useEffect(() => {
    if ((!email && !phone) || !roomId) {
      setCheckingSession(false);
      setError('無効なアクセスです。正しいリンクからアクセスしてください。');
      return;
    }

    const checkExistingSession = async () => {
      try {
        // ルームステータスの事前チェック（削除済みならブロック）
        try {
          const statusRes = await fetch(`/api/enduser/chatrooms/${roomId}/status`);
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            if (!statusData.accessible) {
              setRoomClosed(true);
              setCheckingSession(false);
              return;
            }
          }
        } catch {
          // ステータス確認失敗時は通過させる
        }

        const { token, user } = endUserApiClient.getSession();
        if (!token || !user) {
          setCheckingSession(false);
          return;
        }

        // 電話番号の正規化（数字のみに統一して比較）
        const normalizeDigits = (p: string) => p.replace(/[^\d]/g, '');
        const urlPhone = phone ? normalizeDigits(phone) : '';
        const sessionPhone = user.phoneNumber ? normalizeDigits(user.phoneNumber) : '';

        const isMatch = isPhoneMode
          ? urlPhone && sessionPhone && (urlPhone === sessionPhone || urlPhone.endsWith(sessionPhone.slice(-10)) || sessionPhone.endsWith(urlPhone.slice(-10)))
          : true;

        if (!isMatch) {
          console.log('⏭️ セッションの電話番号不一致、OTP認証が必要');
          setCheckingSession(false);
          return;
        }

        // バックエンドAPIでトークンの有効性を検証
        const response = await fetch(`/api/enduser/chatrooms/${roomId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          console.log('✅ 既存セッション有効、チャットへ直接遷移');
          setSuccess('認証済みです。チャットルームに移動します...');
          setTimeout(() => {
            window.location.href = `/user/chat/${roomId}?token=${token}`;
          }, 1000);
          return;
        }

        // 401 = トークン期限切れ → セッションクリアして通常フローへ
        if (response.status === 401) {
          console.log('⏰ セッション期限切れ、OTP認証が必要');
          endUserApiClient.clearSession();
        }
      } catch (err) {
        console.log('⚠️ セッション確認エラー、OTP認証フローへ:', err);
      }
      setCheckingSession(false);
    };

    checkExistingSession();
  }, [email, phone, roomId, isPhoneMode]);

  // 初回アクセス時の検証のみ（OTP送信は再送ボタンで手動実行）
  useEffect(() => {
    if (checkingSession) return;
    if ((!email && !phone) || !roomId) return;
    if (success) return;

    console.log('📧 OTP検証画面に到達:', { email, phone, roomId });
    setInfo(isPhoneMode ? '「認証コードを送信」を押して認証コードを取得してください。' : '「認証コードを送信」ボタンをクリックして認証コードを取得してください。');
  }, [email, phone, roomId, isPhoneMode, checkingSession, success]);

  useEffect(() => {
    // OTP有効期限のカウントダウン（24時間）
    const endTime = Date.now() + 24 * 60 * 60 * 1000;
    
    const timer = setInterval(() => {
      const remaining = Math.max(0, endTime - Date.now());
      setTimeLeft(remaining);
      
      if (remaining === 0) {
        clearInterval(timer);
        setError('招待の有効期限が切れました。新しい招待メールを要求してください。');
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (ms: number): string => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}時間${minutes}分`;
  };

  const handleOTPChange = (value: string) => {
    // 数字のみ4桁まで
    const numericValue = value.replace(/\D/g, '').slice(0, 4);
    setOtp(numericValue);
    setError('');
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    console.log('🎯 フォーム送信開始:', { email, phone, roomId, otpLength: otp.length });

    try {
      if (!otp || otp.length !== 4) {
        setError('4桁の認証コードを入力してください');
        setLoading(false);
        return;
      }

      console.log('🔐 OTP認証開始:', { email, phone, roomId, otp: '****' });

      const endpoint = isPhoneMode ? '/api/sms/verify-invitation' : '/api/email/verify-otp';
      const payload = isPhoneMode
        ? { roomId, phoneNumber: phone, verificationCode: otp }
        : { roomId, email, otpCode: otp };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        console.log('✅ OTP認証成功');
        setSuccess('認証が完了しました。チャットルームに移動します...');
        
        // セッションにユーザー情報を保存（token, user の順）
        endUserApiClient.setSession(data.sessionToken, data.user);

        // 2秒後にお客様専用チャットルームに移動
        setTimeout(() => {
          window.location.href = `/user/chat/${roomId}?token=${data.sessionToken}`;
        }, 2000);

      } else {
        setError(data.message || 'OTP認証に失敗しました');
      }
    } catch (err) {
      console.error('❌ OTP認証エラー:', err);
      setError('認証中にエラーが発生しました。しばらく後に再試行してください。');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setResendLoading(true);
    setError('');

    try {
      console.log('🔄 OTP送信開始:', { email, phone, roomId, mode: isPhoneMode ? 'sms' : 'email' });
      console.log('🔍 URLパラメータ確認:', {
        rawEmail,
        rawPhone,
        rawRoom,
        email,
        phone,
        roomId,
        searchParamsString: window.location.search
      });
      const endpoint = isPhoneMode ? '/api/sms/resend-invitation' : '/api/email/resend-otp';
      const requestBody = isPhoneMode ? { roomId, phoneNumber: phone } : { roomId, email };
      console.log('📡 APIエンドポイント:', endpoint);
      console.log('📤 リクエストボディ:', requestBody);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      console.log('📡 HTTPレスポンス:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        // エラーレスポンスの詳細を取得
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ エラーレスポンスの詳細:', errorData);
        throw new Error(`HTTP ${response.status}: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      console.log('📡 レスポンスデータ:', data);

      if (data.success) {
        console.log('✅ OTP送信成功');
        setInfo(isPhoneMode ? '認証コードを送信しました。SMSをご確認ください。' : '認証コードを送信しました。メールをご確認ください。');
        setOtp(''); // OTP入力をクリア
        setError(''); // エラーをクリア
        
        // 開発環境でOTPコードを表示
        if (data.developmentOTP) {
          console.log('🔑 開発用OTPコード:', data.developmentOTP);
        }
        
        // 情報メッセージを5秒後に消す
        setTimeout(() => setInfo(''), 5000);
      } else {
        console.log('❌ OTP送信失敗:', data.message);
        setError(data.message || '認証コードの送信に失敗しました');
      }
    } catch (err) {
      console.error('❌ OTP送信エラー:', err);
      const error = err as Error;
      console.error('❌ エラー詳細:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
        setError(`送信中にエラーが発生しました: ${error.message}`);
    } finally {
      setResendLoading(false);
    }
  };

  // セッション確認中
  if (checkingSession) {
    return (
      <Container maxWidth="sm" sx={{ height: '100dvh', overflowY: 'auto', py: 4 }}>
        <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Card sx={{ width: '100%', maxWidth: 500 }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="h6">認証情報を確認中...</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                しばらくお待ちください
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  // ルームが削除済みの場合
  if (roomClosed) {
    return (
      <Container maxWidth="sm" sx={{ height: '100dvh', overflowY: 'auto', py: 4 }}>
        <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', py: 0 }}>
          <Card sx={{ width: '100%', maxWidth: 500 }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'info.main', mb: 2 }} />
              <Typography variant="h5" component="h1" gutterBottom>
                チャット終了
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                このチャットは終了しました。<br />
                この度はお問い合わせいただき、誠にありがとうございました。<br />
                今後ともよろしくお願いいたします。
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  // URLパラメータが無効な場合
  if ((!email && !phone) || !roomId) {
    return (
      <Container maxWidth="sm" sx={{ height: '100dvh', overflowY: 'auto', py: 4 }}>
        <Box
          sx={{
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            py: 0,
          }}
        >
          <Card sx={{ width: '100%', maxWidth: 500 }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <SecurityIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" component="h1" gutterBottom color="error">
                無効なアクセス
              </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              正しい招待リンクからアクセスしてください。
            </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate('/')}
              >
                トップページに戻る
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ height: '100dvh', overflowY: 'auto', py: 4 }}>
      <Box
        sx={{
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          py: 0,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 500 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" component="h1" gutterBottom>
                マイトークへのアクセス
              </Typography>
            </Box>
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                マイトークは株式会社ハイホーが提供するメッセージツールです。お客様のお問い合わせに対して弊社からご連絡をする場合にマイトークを利用しております。セキュリティの観点からメッセージ確認前にご本人様確認をさせていただきます。「認証コードを送信」のボタンから認証コードを取得してください。
              </Typography>
            </Box>

            {/* 連絡先表示（メール or 電話） */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {isPhoneMode ? (
                  <PhoneIcon sx={{ mr: 1, color: 'primary.main' }} />
                ) : (
                  <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                )}
                <Typography variant="body2" color="text.secondary">
                  {isPhoneMode ? '送信先電話番号 (SMS)' : '送信先メールアドレス'}
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight="bold">
                {isPhoneMode ? phone : email}
              </Typography>
            </Box>

            {/* OTP送信機能 - 最優先で表示 */}
            <Box sx={{ textAlign: 'center', mb: 3, p: 3, bgcolor: 'primary.lighter', borderRadius: 2 }}>
              <Typography variant="body1" fontWeight="bold" color="primary.main" sx={{ mb: 2 }}>
                ①まず認証コードを送信してください
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleResendOTP}
                disabled={resendLoading}
                sx={{ mb: 1 }}
                fullWidth
              >
                {resendLoading ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                    送信中...
                  </>
                ) : (
                  '📧 認証コードを送信'
                )}
              </Button>
              <Typography variant="caption" color="text.secondary" display="block">
                「認証コードを送信」を押して認証コードを取得してください。
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }}>
              <Typography variant="caption" color="text.secondary">
                ②メール受信後、以下に入力
              </Typography>
            </Divider>

            {/* 有効期限表示 */}
            {timeLeft > 0 && (
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  有効期限まで: {formatTime(timeLeft)}
                </Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {info && (
              <Alert severity="info" sx={{ mb: 3 }} icon={isPhoneMode ? <PhoneIcon /> : <EmailIcon />}>
                {info}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircleIcon />}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleVerifyOTP}>
              <TextField
                fullWidth
                label="認証コード（4桁）"
                variant="outlined"
                value={otp}
                onChange={(e) => handleOTPChange(e.target.value)}
                required
                placeholder="1234"
                helperText={isPhoneMode ? 'SMSに記載された4桁の数字を入力してください' : 'メールに記載された4桁の数字を入力してください'}
                sx={{ mb: 3 }}
                inputProps={{ 
                  maxLength: 4,
                  style: { 
                    textAlign: 'center', 
                    fontSize: '1.5rem',
                    letterSpacing: '0.5rem'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SecurityIcon />
                    </InputAdornment>
                  ),
                }}
                disabled={loading}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || otp.length !== 4}
                sx={{ mb: 2 }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    認証中...
                  </>
                ) : success ? (
                  'チャットルームに移動中...'
                ) : (
                  '✓ メッセージを確認'
                )}
              </Button>
            </Box>

            {/* 開発環境での注意事項 */}
            {process.env.NODE_ENV === 'development' && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>開発環境:</strong><br />
                  実際のメール送信は行われません。ブラウザのデベロッパーコンソールで認証コードを確認してください。
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default EmailOTPVerification;